import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Button,
  Card,
  CardBody,
  Input,
  Textarea,
  Typography,
} from "@material-tailwind/react";
import PsychBlockedDatesPicker from "../components/admin/PsychBlockedDatesPicker";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { selectCls } from "../constants/formClasses";
import PortalSelect from "../components/PortalSelect";
import { PortalShell } from "../components/PortalShell";
import WeeklyScheduleEditor from "../components/admin/WeeklyScheduleEditor";
import MexicoLocationSelects, {
  locationToPayload,
} from "../components/admin/MexicoLocationSelects";
import {
  defaultWeeklyTemplate,
  normalizeScheduleClient,
  WEEKDAY_LABELS,
} from "../utils/psychSchedule";
import { todayMxYmd } from "../utils/dateMx";
import { CM_BOTH, CM_IN_PERSON, CM_VIRTUAL } from "../utils/consultationModes";

function mergeVersion(versions, effectiveFrom, weekly) {
  const ef = effectiveFrom.slice(0, 10);
  const map = {};
  for (const v of versions || []) {
    map[v.effectiveFrom] = { ...v };
  }
  map[ef] = { effectiveFrom: ef, weekly };
  return Object.values(map).sort((a, b) =>
    a.effectiveFrom.localeCompare(b.effectiveFrom)
  );
}

function summarizeWeekly(weekly) {
  const parts = [];
  for (const block of weekly || []) {
    const ranges = (block.ranges || [])
      .map((r) => `${r.start}–${r.end}`)
      .join(", ");
    parts.push(`${WEEKDAY_LABELS[block.weekday]?.slice(0, 3) || "?"} ${ranges}`);
  }
  return parts.join(" · ") || "—";
}

export default function PortalPsychologistProfile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshMe } = usePortalAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [psych, setPsych] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [address, setAddress] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayEmail, setDisplayEmail] = useState("");
  const [displayPhone, setDisplayPhone] = useState("");
  const [specialtyDescription, setSpecialtyDescription] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newEffectiveFrom, setNewEffectiveFrom] = useState(todayMxYmd());
  const [newWeekly, setNewWeekly] = useState(defaultWeeklyTemplate());
  const [locationSel, setLocationSel] = useState({
    stateCode: "",
    municipalityCode: "",
  });
  const [consultationModes, setConsultationModes] = useState(CM_BOTH);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "psychologist") return;
    let cancelled = false;
    portalApi
      .psychologistProfile()
      .then((r) => {
        const p = r.psychologist;
        if (cancelled || !p) return;
        setPsych(p);
        const n = normalizeScheduleClient(p?.schedule);
        setSchedule(n);
        setAddress((p?.address && String(p.address)) || "");
        setDisplayName((p?.name && String(p.name)) || "");
        setDisplayEmail((p?.email && String(p.email)) || "");
        setDisplayPhone((p?.phone && String(p.phone)) || "");
        setSpecialtyDescription((p?.specialtyDescription && String(p.specialtyDescription)) || "");
        const last = n.versions[n.versions.length - 1];
        if (last?.weekly?.length) {
          setNewWeekly(last.weekly);
        }
        setNewEffectiveFrom(todayMxYmd());
        if (p?.location?.stateCode && p?.location?.municipalityCode) {
          setLocationSel({
            stateCode: p.location.stateCode,
            municipalityCode: p.location.municipalityCode,
          });
        } else {
          setLocationSel({ stateCode: "", municipalityCode: "" });
        }
        const cm = p?.consultationModes;
        setConsultationModes(
          cm === CM_IN_PERSON || cm === CM_VIRTUAL || cm === CM_BOTH ? cm : CM_BOTH
        );
      })
      .catch(() => toast.error("No se pudo cargar tu perfil"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const versions = schedule?.versions || [];

  function addOrReplaceVersion() {
    if (!newEffectiveFrom?.trim()) {
      toast.error("Indica la fecha desde la que aplica el horario");
      return;
    }
    if (!newWeekly?.length) {
      toast.error("Marca al menos un día con horario");
      return;
    }
    const next = mergeVersion(versions, newEffectiveFrom, newWeekly);
    setSchedule({
      blockedDates: schedule?.blockedDates || [],
      versions: next,
    });
    toast.success(
      "Versión añadida al borrador. Pulsa «Guardar mi perfil» cuando quieras enviarla al servidor."
    );
  }

  async function saveAll() {
    if (!psych) return;
    const loc = locationToPayload(locationSel.stateCode, locationSel.municipalityCode);
    if (!loc) {
      toast.error("Selecciona estado y municipio o alcaldía de la consulta presencial.");
      return;
    }
    if (!displayName.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      await portalApi.psychologistProfilePut({
        name: displayName.trim(),
        email: displayEmail.trim() || null,
        phone: displayPhone.trim() || null,
        specialtyDescription: specialtyDescription.trim() || null,
        consultationModes,
        location: loc,
        address: address.trim(),
        schedule: {
          blockedDates: schedule?.blockedDates || [],
          versions: schedule?.versions?.length
            ? schedule.versions
            : normalizeScheduleClient(null).versions,
        },
      });
      toast.success("Perfil actualizado");
      await refreshMe();
      navigate("/psicologo");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function handleProfilePhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !psych) return;
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("La imagen debe pesar como máximo 5 MB");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    const ct = allowed.includes(file.type) ? file.type : "image/jpeg";
    if (!allowed.includes(file.type) && file.type !== "") {
      toast.error("Usa una imagen JPG, PNG o WebP");
      return;
    }
    setUploadingPhoto(true);
    try {
      const r = await portalApi.psychologistPhotoUploadUrl({ contentType: ct });
      const putRes = await fetch(r.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": r.contentType || ct },
      });
      if (!putRes.ok) {
        throw new Error("No se pudo subir la imagen al almacenamiento");
      }
      await portalApi.psychologistProfilePut({ profileImageKey: r.imageKey });
      const refreshed = await portalApi.psychologistProfile();
      setPsych(refreshed.psychologist);
      toast.success("Foto actualizada");
    } catch (err) {
      const msg =
        err?.status === 503
          ? "Las fotos de perfil no están disponibles en este entorno (falta configurar el bucket)."
          : err.message || "No se pudo subir la foto";
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "psychologist") {
    if (user?.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  if (!loading && !psych) {
    return (
      <PortalShell title="Perfil" subtitle="">
        <p className="text-brand-gray text-sm">No se encontró tu perfil de psicólogo.</p>
      </PortalShell>
    );
  }

  return (
    <PortalShell
      title="Mi perfil y horario"
      subtitle="Actualiza tu contacto, ubicación de consulta presencial y el horario que ven los pacientes al agendar."
    >
      {loading && (
        <p className="text-brand-gray text-sm">Cargando…</p>
      )}

      {!loading && psych && schedule && (
        <div className="space-y-8 max-w-3xl">
          <Card className="border border-brand-brown/15">
            <CardBody className="p-5 sm:p-6 space-y-4">
              <Typography className="font-semibold text-brand-dark">
                Datos de contacto
              </Typography>
              <Input
                label="Nombre completo"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="!text-brand-dark"
              />
              <Input
                type="email"
                label="Correo (contacto interno; el cliente no ve tu teléfono)"
                value={displayEmail}
                onChange={(e) => setDisplayEmail(e.target.value)}
                className="!text-brand-dark"
              />
              <Input
                label="Teléfono (uso interno; el cliente no lo ve)"
                value={displayPhone}
                onChange={(e) => setDisplayPhone(e.target.value)}
                className="!text-brand-dark"
              />
            </CardBody>
          </Card>

          <Card className="border border-brand-brown/15">
            <CardBody className="p-5 sm:p-6 space-y-4">
              <Typography className="font-semibold text-brand-dark">
                Foto y especialidad
              </Typography>
              <p className="text-sm text-brand-gray">
                Tu foto y una breve descripción ayudan a los pacientes a conocerte al elegir especialista.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                <div className="shrink-0">
                  {psych.profileImageUrl ? (
                    <img
                      src={psych.profileImageUrl}
                      alt=""
                      className="h-28 w-28 rounded-2xl object-cover border border-brand-brown/15 bg-slate-100"
                    />
                  ) : (
                    <div className="h-28 w-28 rounded-2xl border border-dashed border-brand-brown/25 bg-slate-50 flex items-center justify-center text-xs text-brand-gray text-center px-2">
                      Sin foto
                    </div>
                  )}
                </div>
                <div className="space-y-2 min-w-0">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="block w-full text-sm text-brand-gray file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-lavender/15 file:text-brand-dark"
                    disabled={uploadingPhoto || saving}
                    onChange={handleProfilePhotoChange}
                  />
                  <p className="text-xs text-brand-gray">JPG, PNG o WebP · máx. 5 MB</p>
                </div>
              </div>
              <Textarea
                label="Especialidad o enfoque (visible al agendar)"
                value={specialtyDescription}
                onChange={(e) => setSpecialtyDescription(e.target.value)}
                className="!text-brand-dark !border-blue-gray-200 min-h-[100px]"
              />
            </CardBody>
          </Card>

          <Card className="border border-brand-brown/15">
            <CardBody className="p-5 sm:p-6 space-y-3">
              <Typography className="font-semibold text-brand-dark">
                Modalidad que ofreces a los pacientes
              </Typography>
              <p className="text-sm text-brand-gray">
                Los pacientes solo podrán elegir las opciones que marques al agendar.
              </p>
              <PortalSelect
                className={`${selectCls} max-w-md`}
                value={consultationModes}
                onChange={(e) => setConsultationModes(e.target.value)}
              >
                <option value={CM_BOTH}>Presencial y virtual</option>
                <option value={CM_IN_PERSON}>Solo presencial</option>
                <option value={CM_VIRTUAL}>Solo virtual</option>
              </PortalSelect>
            </CardBody>
          </Card>

          <Card className="border border-brand-brown/15">
            <CardBody className="p-5 sm:p-6 space-y-3">
              <Typography className="font-semibold text-brand-dark">
                Ubicación de consulta presencial (México)
              </Typography>
              <p className="text-sm text-brand-gray">
                Catálogo INEGI (estado y municipio o alcaldía de tu consulta presencial).
              </p>
              <MexicoLocationSelects value={locationSel} onChange={setLocationSel} />
              <Textarea
                label="Dirección de consulta presencial (calle, número, colonia, referencias)"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="!text-brand-dark !border-blue-gray-200 min-h-[88px] mt-4"
              />
            </CardBody>
          </Card>

          <Card className="border border-brand-brown/15">
            <CardBody className="p-5 sm:p-6">
              <Typography className="font-semibold text-brand-dark mb-3">
                Versiones guardadas
              </Typography>
              <ul className="space-y-2 text-sm text-brand-gray">
                {versions.map((v) => (
                  <li key={v.effectiveFrom} className="border-l-2 border-brand-lavender/40 pl-3">
                    <span className="font-medium text-brand-dark">
                      Desde {v.effectiveFrom}
                    </span>
                    <p className="text-xs mt-0.5 break-words [overflow-wrap:anywhere]">{summarizeWeekly(v.weekly)}</p>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          <Card className="border border-brand-brown/15">
            <CardBody className="p-5 sm:p-6 space-y-4">
              <Typography className="font-semibold text-brand-dark">
                Añadir o sustituir una versión
              </Typography>
              <p className="text-sm text-brand-gray">
                La nueva plantilla solo aplica a citas en fechas iguales o posteriores a «Vigente desde».
                Si ya existe una versión con la misma fecha, se reemplaza.
              </p>
              <div>
                <label className="text-xs font-medium text-brand-dark block mb-1">
                  Vigente desde
                </label>
                <input
                  type="date"
                  value={newEffectiveFrom}
                  onChange={(e) => setNewEffectiveFrom(e.target.value)}
                  className={`${selectCls} max-w-xs`}
                />
              </div>
              <WeeklyScheduleEditor weekly={newWeekly} onChange={setNewWeekly} />
              <Button
                type="button"
                variant="outlined"
                className="border-brand-lavender text-brand-lavender normal-case"
                onClick={addOrReplaceVersion}
              >
                Añadir versión al borrador local
              </Button>
            </CardBody>
          </Card>

          <Card className="border-2 border-brand-lavender/20 bg-gradient-to-br from-white to-brand-lilac/40 shadow-sm overflow-hidden">
            <CardBody className="p-5 sm:p-6 space-y-3">
              <div>
                <Typography className="font-semibold text-brand-navy text-base">
                  Días en los que no podrás atender
                </Typography>
                <p className="text-sm text-brand-gray mt-1">
                  Marca los días en que no atenderás. Las reservas futuras respetarán estas excepciones.
                </p>
              </div>
              <PsychBlockedDatesPicker
                variant="self"
                blockedYmd={schedule?.blockedDates || []}
                onChange={(dates) =>
                  setSchedule((s) => ({
                    ...s,
                    blockedDates: dates,
                  }))
                }
              />
            </CardBody>
          </Card>

          <div className="rounded-2xl border border-brand-lavender/20 bg-brand-lilac/30 px-4 py-5 sm:px-6 sm:py-6">
            <p className="text-sm text-brand-gray mb-4 max-w-xl">
              Al guardar se actualizan contacto, ubicación, horarios y estos días bloqueados para las reservas futuras.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center gap-3">
              <Button
                type="button"
                variant="outlined"
                className="border-brand-lavender/40 text-brand-navy normal-case w-full sm:w-auto"
                onClick={() => navigate("/psicologo")}
              >
                Volver al panel
              </Button>
              <Button
                className="bg-brand-lavender hover:bg-brand-navy text-white normal-case shadow-md shadow-brand-lavender/25 w-full sm:w-auto"
                disabled={saving}
                onClick={saveAll}
              >
                {saving ? "Guardando…" : "Guardar mi perfil"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
